import React, { useState, useEffect } from 'react';
import { validateBusinessInfo } from '../../../validators/businessOnboarding';
import {
  BUSINESS_CATEGORIES,
  getSubcategoriesForGroup,
} from '../../../constants/businessCategories';
const OTHER_SUBCATEGORY_LABEL = 'Other / Not listed';

const BusinessInfoStep = ({ data, onChange, validateTrigger = 0 }) => {
  const [formData, setFormData] = useState(data || {});
  const [errors, setErrors] = useState({});

  useEffect(() => {
    setFormData(data || {});
  }, [data]);

  useEffect(() => {
    if (!validateTrigger) return;
    const validation = validateBusinessInfo(formData);
    setErrors(validation.errors);
  }, [validateTrigger, formData]);

  const handleChange = (e) => {
    const { name, value, type, checked } = e.target;
    const newValue = type === 'checkbox' ? checked : value;

    let updatedData = {
      ...formData,
      [name]: newValue,
    };

    // If category group changes, clear category detail if it no longer fits
    if (name === 'categoryGroup') {
      const nextSubcategories = getSubcategoriesForGroup(newValue);
      const currentDetail = updatedData.categoryDetail || '';
      const isValidSelection = nextSubcategories.includes(currentDetail);

      if (!isValidSelection) {
        updatedData = {
          ...updatedData,
          categoryDetail: '',
        };
      }
    }

    setFormData(updatedData);
    onChange(updatedData);

    // Clear error for this field
    if (errors[name]) {
      setErrors((prev) => {
        const newErrors = { ...prev };
        delete newErrors[name];
        return newErrors;
      });
    }
  };

  const handleBlur = () => {
    const validation = validateBusinessInfo(formData);
    setErrors(validation.errors);
  };

  const subcategoryOptions = getSubcategoriesForGroup(formData.categoryGroup || '');
  const categoryDetailInList = subcategoryOptions.includes(formData.categoryDetail || '');
  const categoryDetailSelectValue = categoryDetailInList
    ? (formData.categoryDetail || '')
    : (formData.categoryDetail ? OTHER_SUBCATEGORY_LABEL : '');

  const inputBaseClass =
    'mt-1 w-full rounded-lg border bg-white px-3 py-2 text-sm text-gray-900 shadow-sm outline-none transition focus:ring-2 focus:ring-indigo-100';
  const inputClass = (hasError) =>
    `${inputBaseClass} ${hasError ? 'border-red-500 focus:border-red-500 focus:ring-red-100' : 'border-gray-300 focus:border-indigo-600'}`;
  const labelClass = 'block text-sm font-medium text-gray-700';
  const errorClass = 'mt-1 text-sm text-red-600';

  return (
    <div className="space-y-6">
      <p className="text-sm text-gray-600">
        Tell us about your business. This information will be visible to customers.
      </p>

      <div>
        <label htmlFor="businessName" className={labelClass}>
          Business Name <span className="text-red-500">*</span>
        </label>
        <input
          type="text"
          id="businessName"
          name="businessName"
          value={formData.businessName || ''}
          onChange={handleChange}
          onBlur={handleBlur}
          className={inputClass(!!errors.businessName)}
          placeholder="Enter your business name"
          required
        />
        {errors.businessName && (
          <div className={errorClass}>{errors.businessName}</div>
        )}
      </div>

      <div>
        <label htmlFor="legalBusinessName" className={labelClass}>Legal Business Name (Optional)</label>
        <input
          type="text"
          id="legalBusinessName"
          name="legalBusinessName"
          value={formData.legalBusinessName || ''}
          onChange={handleChange}
          onBlur={handleBlur}
          className={inputClass(!!errors.legalBusinessName)}
          placeholder="Legal name if different from business name"
        />
        {errors.legalBusinessName && (
          <div className={errorClass}>{errors.legalBusinessName}</div>
        )}
      </div>

      <div className="grid grid-cols-1 gap-4 md:grid-cols-2">
        <div>
          <label htmlFor="categoryGroup" className={labelClass}>Category</label>
          <select
            id="categoryGroup"
            name="categoryGroup"
            value={formData.categoryGroup || ''}
            onChange={handleChange}
            className={inputClass(false)}
          >
            <option value="">Select a category</option>
            {BUSINESS_CATEGORIES.map((cat) => (
              <option key={cat.group} value={cat.group}>
                {cat.group}
              </option>
            ))}
          </select>
        </div>

        <div>
          <label htmlFor="categoryDetail" className={labelClass}>Subcategory</label>
          <select
            id="categoryDetail"
            name="categoryDetail"
            value={categoryDetailSelectValue}
            onChange={(e) => {
              const { value } = e.target;
              if (value === OTHER_SUBCATEGORY_LABEL) {
                // Set to the special label so the UI stays in "Other" mode until user types
                const updated = { ...formData, categoryDetail: OTHER_SUBCATEGORY_LABEL };
                setFormData(updated);
                onChange(updated);
                return;
              }
              handleChange(e);
            }}
            disabled={!formData.categoryGroup}
            className={`${inputClass(false)} ${!formData.categoryGroup ? 'cursor-not-allowed bg-gray-50 text-gray-500' : ''}`}
          >
            <option value="">{formData.categoryGroup ? 'Select a subcategory' : 'Select a category first'}</option>
            {subcategoryOptions.map((sub) => (
              <option key={sub} value={sub}>
                {sub}
              </option>
            ))}
          </select>
        </div>
      </div>

      {formData.categoryGroup && categoryDetailSelectValue === OTHER_SUBCATEGORY_LABEL && (
        <div>
          <label htmlFor="categoryDetailOther" className={labelClass}>Describe your subcategory</label>
          <input
            type="text"
            id="categoryDetailOther"
            name="categoryDetailOther"
            value={formData.categoryDetail === OTHER_SUBCATEGORY_LABEL ? '' : (formData.categoryDetail || '')}
            onChange={(e) => {
              const nextValue = e.target.value;
              const updated = {
                ...formData,
                categoryDetail: nextValue ? nextValue : OTHER_SUBCATEGORY_LABEL,
              };
              setFormData(updated);
              onChange(updated);
            }}
            className={inputClass(false)}
            placeholder="Type what you do (e.g., 'Mobile pet grooming', 'Artisan cheese shop')"
            maxLength={120}
          />
        </div>
      )}

      <div>
        <label htmlFor="bio" className={labelClass}>Business Description</label>
        <textarea
          id="bio"
          name="bio"
          value={formData.bio || ''}
          onChange={handleChange}
          onBlur={handleBlur}
          className={`${inputClass(!!errors.bio)} min-h-[120px] resize-y`}
          placeholder="Describe your business (max 1000 characters)"
          maxLength={1000}
        />
        <div className="mt-1 text-right text-xs text-gray-400">
          {(formData.bio || '').length}/1000 characters
        </div>
        {errors.bio && <div className={errorClass}>{errors.bio}</div>}
      </div>

      <div className="grid grid-cols-1 gap-4 md:grid-cols-2">
        <div>
          <label htmlFor="contactPhone" className={labelClass}>Contact Phone</label>
          <input
            type="tel"
            id="contactPhone"
            name="contactPhone"
            value={formData.contactPhone || ''}
            onChange={handleChange}
            onBlur={handleBlur}
            className={inputClass(!!errors.contactPhone)}
            placeholder="+1 (555) 123-4567"
          />
          {errors.contactPhone && (
            <div className={errorClass}>{errors.contactPhone}</div>
          )}
        </div>

        <div>
          <label htmlFor="websiteUrl" className={labelClass}>Website URL</label>
          <input
            type="url"
            id="websiteUrl"
            name="websiteUrl"
            value={formData.websiteUrl || ''}
            onChange={handleChange}
            onBlur={handleBlur}
            className={inputClass(!!errors.websiteUrl)}
            placeholder="https://www.example.com"
          />
          {errors.websiteUrl && <div className={errorClass}>{errors.websiteUrl}</div>}
        </div>
      </div>

      <div className="border-t border-gray-200 pt-6">
        <h3 className="text-base font-semibold text-gray-900">Business Features</h3>
        <p className="mt-1 text-sm text-gray-600">
          Select the features your business will offer on Trippin
        </p>

        <div className="mt-4 space-y-3">
          <label className="flex cursor-pointer items-start gap-3 rounded-lg border border-gray-200 bg-gray-50 p-4 hover:bg-gray-100">
          <input
            type="checkbox"
            id="acceptsReservations"
            name="acceptsReservations"
            checked={formData.acceptsReservations || false}
            onChange={handleChange}
            className="mt-0.5 h-5 w-5"
          />
            <span className="text-sm font-medium text-gray-900">Accepts Reservations</span>
          </label>

          <label className="flex cursor-pointer items-start gap-3 rounded-lg border border-gray-200 bg-gray-50 p-4 hover:bg-gray-100">
          <input
            type="checkbox"
            id="sellsProducts"
            name="sellsProducts"
            checked={formData.sellsProducts || false}
            onChange={handleChange}
            className="mt-0.5 h-5 w-5"
          />
            <span className="text-sm font-medium text-gray-900">Sells Products</span>
          </label>

          <label className="flex cursor-pointer items-start gap-3 rounded-lg border border-gray-200 bg-gray-50 p-4 hover:bg-gray-100">
          <input
            type="checkbox"
            id="hasPhysicalLocation"
            name="hasPhysicalLocation"
            checked={formData.hasPhysicalLocation || false}
            onChange={handleChange}
            className="mt-0.5 h-5 w-5"
          />
          <div>
            <div className="text-sm font-medium text-gray-900">
              Has Physical Location (storefront, office, or venue)
            </div>
            <p className="mt-1 text-sm text-gray-600">
              Check this if your business has a physical address where customers can visit
            </p>
          </div>
          </label>
        </div>
      </div>
    </div>
  );
};

export default BusinessInfoStep;
